import { IRegistrationKiwiSaverEntityState } from './../../state/registration-kiwisaver.state';
import { RegistrationKiwiSaverFacade } from './../../registration-kiwisaver/facade/kiwisaver-registration.facade';
import { SiteIds } from './../../../models/sites';
import { AppConfig } from 'src/app/app.config';
import {
    selectGeneralSelectionDate
} from './../../general-selection/selectors/general-selection.selectors';
import {map, catchError, switchMap, concatMap, filter, take, tap} from "rxjs/operators";
import { Store, select } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Injectable } from "@angular/core";
import { of, Subscription } from "rxjs";
import { IAppState } from 'src/app/store/state/app.state';
import { ClientService } from 'src/app/services/http/client.service';
import * as ClientActions from 'src/app/store/client/actions/client.actions';
import * as ErrorActions from 'src/app/store/global-notification/actions/global-notification.action';
import {EntitySubType} from 'src/app/models/entityTypes';

@Injectable()
export class ClientEffects {
  getKiwiSaverEntitySubscription: Subscription;
  generalSelectionSubscription: Subscription;
  selectedDate: Date;
  
  errorMessage = "Unfortunately portfolio data couldn't be loaded. Apologies for the inconvenience.";

  constructor(
    private actions$: Actions,
    private clientService: ClientService,
    private store: Store<IAppState>,
    private appConfig: AppConfig,
    private registrationKiwiSaverFacade: RegistrationKiwiSaverFacade
  ) {
    this.generalSelectionSubscription = this.store
      .pipe(select(selectGeneralSelectionDate))
      .subscribe((d: Date) => {
        this.selectedDate = d;
      });
  }

  retrieveInitialClientGroups$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.GET_INITIAL_CLIENT_GROUPS),
    switchMap((action: ClientActions.GetInitialClientGroups) => {
      return this.clientService.getClientGroups(this.generateClientSearchModel()).pipe(
        map((res: any) => {
          if (res && res.length > 0) {
            return new ClientActions.SetInitialClientGroups(res);
          } else {
            return new ClientActions.SetInitialClientGroups(null);
          }
        }),
        catchError(err => {
          return of(
            new ClientActions.ClientDataError(this.errorMessage),
            new ErrorActions.NotificationErrorAction(err)
          );
        })
      );
    })
  ));

  retrieveClientGroups$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.GET_CLIENT_GROUPS),
    switchMap((action: ClientActions.GetClientGroups) => {
      return this.clientService.getClientGroups(action.payload).pipe(
        map((res: any) => {
          if (res && res.length > 0) {
            return new ClientActions.SetClientGroups(res);
          } else {
            return new ClientActions.SetClientGroups(null);
          }
        }),
        catchError(err => {
          return of(
            new ClientActions.ClientDataError(this.errorMessage),
            new ErrorActions.NotificationErrorAction(err)
          );
        })
      );
    })
  ));

  retrieveClientGroupEntities$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.SET_SELECTED_CLIENT_GROUP),
    switchMap((action: ClientActions.SetSelectedClientGroup) => {
      const Id = action.payload.selectedClient.GroupId;
      return this.clientService
        .getClientGroupEntities(Id, action.payload.date)
        .pipe(
          concatMap((res: any) => {
            if (res) {
              const canMultiSelect = !AppConfig.settings.features.dateSelector.disableAggregation;
              const payload = {
                members: res,
                selectedDate: action.payload.date,
                multiSelect: canMultiSelect
              };
              return [
                new ClientActions.GetClientGroupEntitiesSuccess(),
                new ClientActions.SetSelectedClientGroupEntities(payload)
              ];

            }
          }),
          catchError(err => {
            return of(
              new ClientActions.GetClientGroupEntitiesFail(),
              new ClientActions.ClientDataError(this.errorMessage),
              new ErrorActions.NotificationErrorAction(
                  this.errorMessage
              )
            );
          })
        );
    })
  ));

  retrieveSingleEntityPortfolioValue$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.SET_SELECTED_SINGLE_ENTITY_CLIENT),
    switchMap((action: ClientActions.SetSelectedSingleEntityClient) => {
      const Id = action.payload.selectedClient.EntityId;
      return this.clientService
        .getSingleEntityPortfolioValue(Id, action.payload.date)
        .pipe(
          concatMap((res: any) => {
            if (res) {
              const payload = {
                members: [
                  {
                    EntityId: action.payload.selectedClient.EntityId,
                    Name: action.payload.selectedClient.GroupName,
                    NetAssetValue: res,
                    EntitySubType: action.payload.selectedClient.EntitySubType,
                    EntityType: action.payload.selectedClient.EntityType
                  }
                ],
                selectedDate: action.payload.date
              };
              return [
                new ClientActions.SetSelectedClientGroupEntities(payload),
                new ClientActions.GetSingleEntityPortfolioValueSuccess()
              ];
            }
          }),
          catchError(err => {
            return of(
              new ClientActions.GetSingleEntityPortfolioValueFail(),
              new ClientActions.ClientDataError(this.errorMessage),
              new ErrorActions.NotificationErrorAction(
                err.error.error_description
              )
            );
          })
        );
    })
  ));

  updateEntityDateData$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.UPDATE_ENTITY_DATE_DATA),
    switchMap((action: ClientActions.UpdateEntityDateData) => {
      return this.clientService
        .getClientGroupEntities(
          action.payload.selectedClient.GroupId,
          action.payload.date
        )
        .pipe(
          map((res: any) => {
            if (res) {
              const payload = {
                members: res,
                selectedDate: action.payload.date,
                updateEntityDateData: true
              };
              return new ClientActions.SetSelectedClientGroupEntities(payload);
            }
          }),
          catchError(err => {
            return of(
              new ClientActions.ClientDataError(this.errorMessage),
              new ErrorActions.NotificationErrorAction(
                err.error.error_description
              )
            );
          })
        );
    })
  ));

  getAvailablePositions$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.GET_AVAILABLE_POSITIONS),
    switchMap((action: ClientActions.GetAvailablePositions) => {
      return this.clientService.getAvailablePositions(action.payload).pipe(
        map((res: any) => {
          if (res) {
            const payload = res;
            return new ClientActions.SetAvailablePositions(payload);
          }
        }),
        catchError(err => {
          return of(
            new ClientActions.ClientDataError(this.errorMessage),
            new ErrorActions.NotificationErrorAction(
              err.error.error_description
            )
          );
        })
      );
    })
  ));

  // single investor
  retrievePortfolioEntities$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.GET_CLIENT_INVESTOR_ENTITIES),
    switchMap((action: ClientActions.GetClientInvestorEntities) => {
      return this.clientService.getClientInvestorEntities(action.payload).pipe(
        map((res: any) => {
          if (res) {
            const canMultiSelect = !AppConfig.settings.features.dateSelector.disableAggregation;
            const payload = {
              members: res,
              selectedDate: action.payload,
              updateEntityDateData: false,
              multiSelect: canMultiSelect
            };
            const siteId = this.appConfig.getEnvironment().siteId;
            if (siteId && siteId === SiteIds.INVESTNOW) {
              // for single KS investors, we need to filter out all non kiwisaver members
              return new ClientActions.FilterRetrievedKiwiSaverInvestorEntities(payload);
            } else {
              return new ClientActions.SetSelectedClientGroupEntities(payload);
            }
          }
        }),
        catchError(err => {
          return of(
            new ClientActions.ClientDataError(this.errorMessage),
            new ErrorActions.NotificationErrorAction(
              err.error.error_description
            )
          );
        })
      );
    })
  ));

  filterRetrievedKSPortfolioEntities$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.FILTER_RETRIEVED_KIWISAVER_INVESTOR_ENTITIES),
    map((action: ClientActions.FilterRetrievedKiwiSaverInvestorEntities) => {
      if (this.getKiwiSaverEntitySubscription) {
        this.getKiwiSaverEntitySubscription.unsubscribe();
      }
      this.getKiwiSaverEntitySubscription = this.registrationKiwiSaverFacade.getKiwisaverEntity().pipe(
        filter((entity) => entity != null),
        take(1),
      ).subscribe((entity: IRegistrationKiwiSaverEntityState) => {
        const members = action.payload.members;
        const filteredMembersByKS = members.filter((entityMember) => entityMember.EntityId === entity.entityId);
        const payloadKS = {
          members: filteredMembersByKS,
          selectedDate: action.payload,
          updateEntityDateData: false,
          multiSelect: false
        };
        this.store.dispatch(new ClientActions.SetSelectedClientGroupEntities(payloadKS));
      });
    })
  ), { dispatch: false });

  private generateClientSearchModel(){
    const siteId = this.appConfig.getEnvironment().siteId;
    if (siteId && (siteId === SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL)) {
      return {
        query: "",
        entitySubType: EntitySubType.KiwiSaverInvestor
      };
    }
    
    return {
      query: "",
      entitySubType: null
    };
  }
}

